import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../ThemeContext';
import PlanDateField from './PlanDateField';

function yearFromAgeBucket(bucket, today) {
  const year = today.getFullYear();
  const map = { '0-5': 2, '6-10': 8, '11-15': 13, '16+': 20 };
  if (!bucket || bucket === 'unsure' || bucket === 'unknown') return null;
  if (Object.prototype.hasOwnProperty.call(map, bucket)) return year - map[bucket];
  const numeric = Number(bucket);
  if (Number.isFinite(numeric) && numeric >= 1900 && numeric <= year + 1) return numeric;
  return null;
}

const AGES = [
  { id: '0-5', label: '0–5 years' },
  { id: '6-10', label: '6–10 years' },
  { id: '11-15', label: '11–15 years' },
  { id: '16+', label: '16+ years' },
  { id: 'unsure', label: 'Not sure' },
];

const FILTERS = [
  { id: '1in', label: 'Thin 1"' },
  { id: '4in', label: 'Thick 4–5"' },
  { id: 'washable', label: 'Washable' },
  { id: 'unknown', label: 'Not sure' },
];

const POLICY_LENGTHS = [
  { id: '6', label: '6-month policy', hint: 'Renews twice a year' },
  { id: '12', label: '12-month policy', hint: 'Renews every year' },
];

function Tile({ selected, label, hint, onClick, brandColor, colors, isDark, block }) {
  const idleBorder = isDark ? '#6b7280' : '#d5e4e4';
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        width: block ? '100%' : undefined,
        minHeight: '52px',
        padding: '0.75rem 0.65rem',
        borderRadius: '0.75rem',
        border: selected ? `2px solid ${brandColor}` : `1px solid ${idleBorder}`,
        background: selected ? '#e7f7f7' : colors.surface,
        color: selected ? '#083838' : colors.text,
        fontWeight: 650,
        fontSize: '1rem',
        cursor: 'pointer',
        lineHeight: 1.25,
      }}
    >
      <span style={{ display: 'block' }}>{label}</span>
      {hint ? (
        <span style={{ display: 'block', fontWeight: 500, fontSize: '0.8rem', marginTop: '0.15rem' }}>{hint}</span>
      ) : null}
    </button>
  );
}

const SAVED_NOTICE_MS = 4000;

function planSignature(values) {
  return [
    values.renewal || '',
    values.term || '',
    values.filter || '',
    values.roof || '',
    values.ac || '',
    values.floodNone ? 'none' : (values.floodDate || ''),
    values.floodNone ? '' : (values.floodTerm || ''),
    values.warrantyNone ? 'none' : (values.warrantyDate || ''),
    values.warrantyNone ? '' : (values.warrantyTerm || ''),
  ].join('\u0001');
}

function Question({ children, colors }) {
  return (
    <div style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem', color: colors.text }}>
      {children}
    </div>
  );
}

/**
 * Four signup questions on one skippable step. Not part of the AI chat.
 */
export default function HomeownerPlanStep({
  initial,
  onSave,
  onSkip,
  saving,
  brandColor = '#59c4c4',
  showSkip = true,
  saveLabel = 'Save and finish',
  showPolicies = false,
  welcome = false,
  tasksReady = false,
  pinSave = false,
  readOnly = false,
  hideRoofAge = false,
  hideFilter = false,
  hideAcAge = false,
}) {
  const { colors, isDark } = useTheme();
  const today = new Date();
  const [renewal, setRenewal] = useState((initial && initial.insurance_renewal_date) || '');
  const [term, setTerm] = useState(initial && initial.insurance_term_months ? String(initial.insurance_term_months) : '');
  const [filter, setFilter] = useState((initial && initial.filter_type) || '');
  const [roof, setRoof] = useState('');
  const [ac, setAc] = useState('');
  const [floodDate, setFloodDate] = useState((initial && initial.flood_renewal_date) || '');
  const [floodTerm, setFloodTerm] = useState(initial && initial.flood_term_months ? String(initial.flood_term_months) : '');
  const [floodDirty, setFloodDirty] = useState(false);
  const [floodNone, setFloodNone] = useState(false);
  const [warrantyDate, setWarrantyDate] = useState((initial && initial.warranty_renewal_date) || '');
  const [warrantyTerm, setWarrantyTerm] = useState(initial && initial.warranty_term_months ? String(initial.warranty_term_months) : '');
  const [warrantyDirty, setWarrantyDirty] = useState(false);
  const [warrantyNone, setWarrantyNone] = useState(false);
  const [savedNotice, setSavedNotice] = useState('');
  const [savedSignature, setSavedSignature] = useState(() => planSignature({
    renewal: (initial && initial.insurance_renewal_date) || '',
    term: initial && initial.insurance_term_months ? String(initial.insurance_term_months) : '',
    filter: (initial && initial.filter_type) || '',
    roof: '',
    ac: '',
    floodDate: (initial && initial.flood_renewal_date) || '',
    floodTerm: initial && initial.flood_term_months ? String(initial.flood_term_months) : '',
    floodNone: false,
    warrantyDate: (initial && initial.warranty_renewal_date) || '',
    warrantyTerm: initial && initial.warranty_term_months ? String(initial.warranty_term_months) : '',
    warrantyNone: false,
  }));
  const [saveInView, setSaveInView] = useState(true);
  const saveAnchorRef = useRef(null);
  const signature = planSignature({
    renewal,
    term,
    filter,
    roof,
    ac,
    floodDate,
    floodTerm,
    floodNone,
    warrantyDate,
    warrantyTerm,
    warrantyNone,
  });
  const dirty = signature !== savedSignature;

  useEffect(() => {
    if (!savedNotice) return undefined;
    const timer = setTimeout(() => setSavedNotice(''), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [savedNotice]);

  useEffect(() => {
    const node = saveAnchorRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setSaveInView(Boolean(entry && entry.isIntersecting));
    }, {
      root: null,
      threshold: 0,
      rootMargin: pinSave ? '0px 0px -88px 0px' : '0px',
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [pinSave]);

  const touch = () => setSavedNotice('');

  const save = async () => {
    const fields = {};
    if (renewal) fields.insurance_renewal_date = renewal;
    if (term === '6' || term === '12') fields.insurance_term_months = Number(term);
    if (!hideFilter && filter) fields.filter_type = filter;
    const roofYear = yearFromAgeBucket(roof, today);
    const acYear = yearFromAgeBucket(ac, today);
    if (!hideRoofAge && roof && roof !== 'unsure') {
      fields.roof_year = roofYear;
      if (roofYear) fields.roof_install_year = roofYear;
    }
    if (!hideAcAge && ac && ac !== 'unsure') {
      fields.ac_year = acYear;
      if (acYear) fields.hvac_install_years = [acYear];
    }
    if (showPolicies && (floodDirty || floodDate || floodNone)) {
      fields.flood_renewal_date = floodNone ? null : (floodDate || null);
      fields.flood_term_months = !floodNone && (floodTerm === '6' || floodTerm === '12') ? Number(floodTerm) : null;
    }
    if (showPolicies && (warrantyDirty || warrantyDate || warrantyNone)) {
      fields.warranty_renewal_date = warrantyNone ? null : (warrantyDate || null);
      fields.warranty_term_months = !warrantyNone && (warrantyTerm === '6' || warrantyTerm === '12') ? Number(warrantyTerm) : null;
    }
    fields.onboarding_completed_at = new Date().toISOString();
    try {
      const result = await onSave(fields);
      if (result === false) return;
      setSavedSignature(signature);
      setSavedNotice('Saved. Your reminders use these answers.');
    } catch (error) {
      setSavedNotice('');
    }
  };

  const policyTiles = (current, choose) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
      {POLICY_LENGTHS.map((item) => (
        <Tile
          key={item.id}
          selected={current === item.id}
          label={item.label}
          hint={item.hint}
          brandColor={brandColor}
          colors={colors}
          isDark={isDark}
          onClick={() => choose(item.id)}
        />
      ))}
    </div>
  );

  const canSave = dirty && !saving && !readOnly;
  if (readOnly) {
    const show = (value) => (value ? String(value) : 'Not set');
    return (
      <div style={{ color: colors.text, lineHeight: 1.5 }}>
        <p style={{ marginTop: 0, color: colors.textSecondary }}>
          These answers stay with this archived home.
        </p>
        <p style={{ margin: '0.35rem 0' }}>Homeowners insurance renews: {show(initial && initial.insurance_renewal_date)}</p>
        <p style={{ margin: '0.35rem 0' }}>Filter: {show(initial && initial.filter_type)}</p>
        <p style={{ margin: '0.35rem 0' }}>Flood insurance renews: {show(initial && initial.flood_renewal_date)}</p>
        <p style={{ margin: '0.35rem 0' }}>Warranty renews: {show(initial && initial.warranty_renewal_date)}</p>
      </div>
    );
  }
  const saveBar = (
    <div
      ref={saveAnchorRef}
      data-testid="plan-save-bar"
      style={{
        marginTop: '0.5rem',
        scrollMarginBottom: pinSave
          ? 'calc(72px + env(safe-area-inset-bottom, 0px) + 16px)'
          : 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      }}
    >
      {savedNotice ? (
        <p role="status" style={{ margin: '0 0 0.5rem', color: colors.text, fontWeight: 650 }}>
          {savedNotice}
        </p>
      ) : null}
      <button
        type="button"
        onClick={save}
        disabled={!canSave}
        style={{
          width: '100%',
          minHeight: '52px',
          border: 'none',
          borderRadius: '0.75rem',
          background: brandColor,
          color: '#083838',
          fontWeight: 700,
          fontSize: '1rem',
          marginBottom: showSkip ? '0.5rem' : 0,
          opacity: canSave ? 1 : 0.45,
          cursor: canSave ? 'pointer' : 'not-allowed',
        }}
      >
        {saving ? 'Saving...' : saveLabel}
      </button>
      {showSkip ? (
        <button
          type="button"
          onClick={onSkip}
          disabled={saving}
          style={{
            width: '100%',
            minHeight: '48px',
            marginTop: '0.5rem',
            border: `1px solid ${isDark ? '#6b7280' : '#d5e4e4'}`,
            borderRadius: '0.75rem',
            background: 'transparent',
            color: colors.text,
            fontSize: '1rem',
          }}
        >
          Skip for now
        </button>
      ) : null}
    </div>
  );

  return (
    <div style={{ paddingBottom: pinSave ? 0 : 'env(safe-area-inset-bottom)', color: colors.text }}>
      <p style={{ marginTop: 0, color: colors.textSecondary, lineHeight: 1.45 }}>
        {tasksReady
          ? `${initial && initial.first_name ? `${initial.first_name}, a` : 'A'} year of tasks is already on your list. These answers set your insurance reminder, how often to change the filter, and how often to check an older roof or AC. Skip any of them.`
          : welcome
            ? 'These answers set your insurance reminder, how often to change the filter, and how often to check an older roof or AC. Skip any of them.'
            : showPolicies
              ? 'These answers set your insurance, flood, and warranty reminders, how often to change the filter, and how often to check an older roof or AC. Leave a date blank to keep it unchanged.'
              : 'Four quick questions. Skip any of them. You can change the answers later in Property.'}
      </p>
      <section aria-label="Homeowners insurance">
        <Question colors={colors}>When does your homeowners insurance renew?</Question>
        <PlanDateField
          id="homeowners-renewal"
          label="When does your homeowners insurance renew?"
          value={renewal}
          colors={colors}
          onChange={(event) => { setRenewal(event.target.value); touch(); }}
        />
        {policyTiles(term, (next) => { setTerm(next); touch(); })}
      </section>
      {hideFilter ? null : (
        <div>
          <Question colors={colors}>What AC filter do you use?</Question>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
            {FILTERS.map((item) => (
              <Tile
                key={item.id}
                selected={filter === item.id}
                label={item.label}
                brandColor={brandColor}
                colors={colors}
                isDark={isDark}
                onClick={() => { setFilter(item.id); touch(); }}
              />
            ))}
          </div>
        </div>
      )}
      {showPolicies ? (
        <div>
          <section aria-label="Flood insurance">
            <Question colors={colors}>When does your flood insurance renew?</Question>
            <PlanDateField
              id="flood-renewal"
              label="When does your flood insurance renew?"
              value={floodNone ? '' : floodDate}
              colors={colors}
              onChange={(event) => {
                setFloodDate(event.target.value);
                setFloodNone(false);
                setFloodDirty(true);
                touch();
              }}
            />
            <div style={{ marginBottom: '0.75rem' }}>
              <Tile
                block
                selected={floodNone}
                label="I don't have this"
                brandColor={brandColor}
                colors={colors}
                isDark={isDark}
                onClick={() => {
                  setFloodNone(true);
                  setFloodDate('');
                  setFloodTerm('');
                  setFloodDirty(true);
                  touch();
                }}
              />
            </div>
            {policyTiles(floodNone ? '' : floodTerm, (next) => {
              setFloodTerm(next);
              setFloodNone(false);
              setFloodDirty(true);
              touch();
            })}
          </section>
          <section aria-label="Home warranty">
            <Question colors={colors}>When does your home warranty expire?</Question>
            <PlanDateField
              id="warranty-renewal"
              label="When does your home warranty expire?"
              value={warrantyNone ? '' : warrantyDate}
              colors={colors}
              onChange={(event) => {
                setWarrantyDate(event.target.value);
                setWarrantyNone(false);
                setWarrantyDirty(true);
                touch();
              }}
            />
            <div style={{ marginBottom: '0.75rem' }}>
              <Tile
                block
                selected={warrantyNone}
                label="I don't have this"
                brandColor={brandColor}
                colors={colors}
                isDark={isDark}
                onClick={() => {
                  setWarrantyNone(true);
                  setWarrantyDate('');
                  setWarrantyTerm('');
                  setWarrantyDirty(true);
                  touch();
                }}
              />
            </div>
            {policyTiles(warrantyNone ? '' : warrantyTerm, (next) => {
              setWarrantyTerm(next);
              setWarrantyNone(false);
              setWarrantyDirty(true);
              touch();
            })}
          </section>
        </div>
      ) : null}
      {hideRoofAge ? null : (
        <div>
          <Question colors={colors}>About how old is your roof?</Question>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
            {AGES.map((item) => (
              <Tile
                key={`roof-${item.id}`}
                selected={roof === item.id}
                label={item.label}
                brandColor={brandColor}
                colors={colors}
                isDark={isDark}
                onClick={() => { setRoof(item.id); touch(); }}
              />
            ))}
          </div>
        </div>
      )}
      {hideAcAge ? null : (
        <div>
          <Question colors={colors}>About how old is your AC?</Question>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
            {AGES.map((item) => (
              <Tile
                key={`ac-${item.id}`}
                selected={ac === item.id}
                label={item.label}
                brandColor={brandColor}
                colors={colors}
                isDark={isDark}
                onClick={() => { setAc(item.id); touch(); }}
              />
            ))}
          </div>
        </div>
      )}
      {saveBar}
      {dirty && !saveInView ? (
        <button
          type="button"
          className="plan-unsaved-hint"
          onClick={() => {
            const node = saveAnchorRef.current;
            if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 900,
            width: 'auto',
            maxWidth: 'calc(100% - 2rem)',
            margin: 0,
            padding: '0.35rem 0.9rem',
            borderRadius: '999px',
            border: `1px solid ${isDark ? '#4b5563' : '#d5e4e4'}`,
            background: isDark ? '#2a2a2a' : '#ffffff',
            color: isDark ? '#f9fafb' : '#1a1a1a',
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.12)',
            fontSize: '0.85rem',
            fontWeight: 650,
            lineHeight: 1.3,
            cursor: 'pointer',
          }}
        >
          Unsaved answers
        </button>
      ) : null}
      <style>{`
        .plan-unsaved-hint {
          bottom: ${pinSave
            ? 'calc(72px + env(safe-area-inset-bottom, 0px) + 8px)'
            : 'calc(env(safe-area-inset-bottom, 0px) + 16px)'};
        }
      `}</style>
    </div>
  );
}
